import { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Button,
  Center,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';

import { AddCircleOutline, SearchNormalOutline } from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import CreatePayCalendarModal from 'app/components/PayCalendars/CreatePayCalendar/CreatePayCalendarModal';
import CreatePayCalendarOverviewModal from 'app/components/PayCalendars/CreatePayCalendar/CreatePayCalendarOverviewModal';
import ArchivePayCalendarModal from 'app/components/PayCalendars/ArchivePayCalendar/ArchivePayCalendarModal';
import DeletePayCalendarModal from 'app/components/PayCalendars/DeletePayCalendar/DeletePayCalendarModal';

import { defaultPageSize } from 'app/constants/table';

import useFetchPayCalendarsPage from 'app/hooks/useFetchPayCalendarsPage';
import useDebounce from 'app/hooks/useDebounce';

import { useManagingCompanyStore } from 'app/store';

import { getPayCalendarColumns } from 'app/utils/table/payCalendars';
import { getPayCalendarProfilePath } from 'app/utils/routes';
import { PayCalendarRow, Table } from 'app/components/UI/Table';

function PayCalendars() {
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const {
    isOpen: isCreatePayCalendarOpen,
    onOpen: onOpenCreatePayCalendar,
    onClose: onCloseCreatePayCalendar,
  } = useDisclosure();

  const {
    isOpen: isCreatePayCalendarOverviewOpen,
    onOpen: onOpenCreatePayCalendarOverview,
    onClose: onCloseCreatePayCalendarOverview,
  } = useDisclosure();

  const {
    isOpen: isDeletePayCalendarsOpen,
    onOpen: onOpenDeletePayCalendars,
    onClose: onCloseDeletePayCalendars,
  } = useDisclosure();

  const {
    isOpen: isArchivePayCalendarsOpen,
    onOpen: onOpenArchivePayCalendars,
    onClose: onCloseArchivePayCalendars,
  } = useDisclosure();

  const { company } = useManagingCompanyStore.getState();

  const [payCalendarsActionIds, setPayCalendarsActionIds] = useState<string[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('query') || '');

  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const onSearchQueryChangedDebounced = useDebounce(handleSearchQueryChange);

  const [sorting, setSorting] = useState<SortingState>(() => [
    ...(searchParams.get('sortBy')
      ? [
          {
            id: searchParams.get('sortBy') as string,
            desc: searchParams.get('sort') === 'DESC',
          },
        ]
      : []),
  ]);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data: payCalendars, isLoading: isLoadingPayCalendars } = useFetchPayCalendarsPage({
    companyId: [company?.id || ''],
    query: searchQuery,
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const selectPayCalendar = (payCalendar: PayCalendarRow) => {
    navigate(getPayCalendarProfilePath(payCalendar.id));
  };

  const handleOpenDeletePayCalendars = (ids: number[]) => {
    const payCalendarIds = ids.map((id) => payCalendars?.list[id]?.id || '');

    setPayCalendarsActionIds(payCalendarIds);
    onOpenDeletePayCalendars();
  };

  const handleOpenArchivePayCalendars = (ids: number[]) => {
    setPayCalendarsActionIds(ids.map((id) => payCalendars?.list[id]?.id || ''));
    onOpenArchivePayCalendars();
  };

  const handleArchivePayCalendars = () => {
    queryClient.invalidateQueries({ queryKey: [`fetchPayCalendarsPage`] });
    queryClient.invalidateQueries({ queryKey: ['fetchPayCalendarsForEmployee'] });

    setPayCalendarsActionIds([]);
    setRowSelection({});
  };

  const handleDeletePayCalendars = () => {
    const totalPages = Math.ceil((payCalendars?.total || 0) / pageSize);

    if (
      totalPages > 1 &&
      pageIndex === totalPages - 1 &&
      payCalendarsActionIds.length === payCalendars?.list.length
    ) {
      setPagination({ pageIndex: pageIndex - 1, pageSize: defaultPageSize });
    }

    queryClient.invalidateQueries({ queryKey: [`fetchPayCalendarsPage`] });

    setPayCalendarsActionIds([]);
    setRowSelection({});
  };

  const actions = {
    select: selectPayCalendar,
    archive: handleOpenArchivePayCalendars,
    delete: handleOpenDeletePayCalendars,
  };

  const bulkActions = {
    archive: { action: handleOpenArchivePayCalendars },
    delete: { action: handleOpenDeletePayCalendars },
  };

  useEffect(() => {
    setSearchParams(
      {
        page: `${pageIndex + 1}`,
        ...(searchQuery && { query: searchQuery }),
        ...(sorting[0] && { sortBy: sorting[0]?.id, sort: sorting[0]?.desc ? 'DESC' : 'ASC' }),
      },
      { replace: true }
    );
  }, [pageIndex, searchQuery, sorting, setSearchParams]);

  return (
    <AuthenticatedPageWrapper title="Pay calendars">
      <HStack>
        <Text as="h2" textStyle="h2" color="brand.darkPurple">
          Pay calendars
        </Text>
        <Spacer />
        <Button
          leftIcon={<AddCircleOutline boxSize="20px" />}
          size="sm"
          onClick={onOpenCreatePayCalendar}
        >
          Create new
        </Button>
        <CreatePayCalendarModal
          isOpen={isCreatePayCalendarOpen}
          onClose={onCloseCreatePayCalendar}
          onCreate={onOpenCreatePayCalendarOverview}
        />
        <CreatePayCalendarOverviewModal
          isOpen={isCreatePayCalendarOverviewOpen}
          onClose={onCloseCreatePayCalendarOverview}
          onEdit={onOpenCreatePayCalendar}
        />
        <ArchivePayCalendarModal
          selectedPayCalendars={payCalendarsActionIds}
          isOpen={isArchivePayCalendarsOpen}
          onClose={onCloseArchivePayCalendars}
          onArchive={handleArchivePayCalendars}
        />
        <DeletePayCalendarModal
          selectedPayCalendars={payCalendarsActionIds}
          isOpen={isDeletePayCalendarsOpen}
          onClose={onCloseDeletePayCalendars}
          onDelete={handleDeletePayCalendars}
        />
      </HStack>
      <Flex justify="space-between" gap="30px" alignItems="center">
        <Text my="10px" as="h5" textStyle="h5SemiBold" color="brand.darkGray">
          Pay calendars list
        </Text>
        <Spacer />
        <InputGroup
          maxW="278px"
          aria-disabled={!searchQuery.length && !payCalendars?.list.length}
          color="brand.darkGray"
          _disabled={{ color: 'system.disabled' }}
        >
          <Input
            isDisabled={!searchQuery.length && !payCalendars?.list.length}
            onChange={(event) => onSearchQueryChangedDebounced(event.target.value)}
            type="text"
            placeholder="Search"
          />
          <InputRightElement w="56px" h="full">
            <SearchNormalOutline boxSize="24px" />
          </InputRightElement>
        </InputGroup>
      </Flex>
      {isLoadingPayCalendars || !payCalendars ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <Table
          columns={getPayCalendarColumns(actions)}
          data={payCalendars.list}
          total={payCalendars.total}
          pageNumber={payCalendars.pageNumber}
          pageSize={payCalendars.pageSize}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          getRowId={(payCalendar) => payCalendar.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={bulkActions}
          bulkActionsType="payCalendar"
        />
      )}
    </AuthenticatedPageWrapper>
  );
}

export default PayCalendars;
